import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/ui/atoms/BackButton';
import { getAuth, fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth } from '../firebase.js';
import fetchWithAuth from '../utils/fetchWithAuth';
import FileUploader from '../components/FileUploader';
import { Input } from '../components/ui/atoms/input/Input';
import { Button } from '../components/ui/atoms/Button/Button';
import SectionHeader from '../components/ui/atoms/SectionTitle/SectionHeader';

const AccountSettings = () => {
  const navigate = useNavigate();
  const firebaseAuth = getAuth();
  const uid = firebaseAuth.currentUser?.uid!;

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [methods, setMethods] = useState<string[]>([]);
  const [avatarURL, setAvatarURL] = useState('');
  const [bio, setBio] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const u = await fetchWithAuth(`/api/users/${uid}`);
      setName(u.name || '');
      setUsername(u.username || '');
      setAvatarURL(u.avatarUrl || '');
      setBio(u.bio || '');

      // fetch email & sign-in methods
      const userEmail = firebaseAuth.currentUser?.email || '';
      setEmail(userEmail);
      if (userEmail) {
        const m = await fetchSignInMethodsForEmail(firebaseAuth, userEmail);
        setMethods(m);
      }
    })();
  }, [uid]);

  useEffect(() => {
    setDirty(true);
  }, [name, username, avatarURL, bio]);

  const handleSaveAll = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await fetchWithAuth(`/api/users/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          username,
          avatarUrl: avatarURL,
          bio,
        }),
      });
      setDirty(false);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
      navigate('/app/settings');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-50 bg-white h-[64px] flex items-center justify-between px-4 border-b border-gray-200">
        <BackButton 
          onClick={() => navigate('/app/settings')} 
        />
        <Button
          variant="header"
          onClick={handleSaveAll}
          disabled={!dirty || saving}
          className="ml-auto"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* Content */}
      <main className="flex-grow overflow-y-auto px-2 pb-20">
        {/* Avatar */}
        <section>
          <SectionHeader title="Avatar" />
          <div className="flex items-center space-x-4 px-4 py-2">
            <div className="w-16 h-16 relative">
              <img
                src={avatarURL || '/default-avatar.png'}
                alt="Avatar"
                className="w-16 h-16 rounded-full object-cover border"
              />
              {avatarURL && (
                <button
                  type="button"
                  onClick={() => setAvatarURL('')}
                  className="absolute -top-1 -right-1 bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-black rounded-full w-5 h-5 flex items-center justify-center shadow-sm transition"
                  aria-label="Remove avatar"
                  title="Remove avatar"
                >
                  &times;
                </button>
              )}
            </div>
            <FileUploader
              value={avatarURL}
              onUpload={(url) => {
                setAvatarURL(url);
                setDirty(true);
              }}
              type="avatar"
            />
          </div>
        </section>

        {/* Name */}
        <section>
          <SectionHeader title="Name" />
          <div className="px-4 py-2">
            <Input
              value={name}
              setValue={setName}
              placeholder="Enter your name"
            />
          </div>
        </section>

        {/* Username */}
        <section>
          <SectionHeader title="Username" />
          <div className="px-4 py-2">
            <Input
              value={username}
              setValue={setUsername}
              placeholder="Choose a username"
            />
          </div>
        </section>

      {/* Email (read‐only) */}
      <section>
        <SectionHeader title="Email" />
        <div className="px-4 py-2">
          <input
            type="text"
            value={email}
            readOnly
            className="w-full border-0 border-b border-black focus:outline-none focus:border-black py-1 bg-transparent"
            placeholder="email@example.com"
          />
        </div>
      </section>

      {/* Sign-in Method */}
      {/* <section>
        <SectionHeader title="Sign-in Method" />
        <div className="px-4 py-2 text-gray-700">
          {methods.length
            ? methods.map(m =>
                m === 'password' ? 'Email/Password' :
                m === 'google.com' ? 'Google' :
                m === 'apple.com' ? 'Apple' :
                m
              ).join(', ')
            : '—'}
        </div>
      </section> */}

        {/* Bio */}
        <section>
          <SectionHeader title="Bio" />
          <div className="px-4 py-2">
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              className="w-full border-0 border-b border-black focus:outline-none focus:border-black py-1"
              placeholder="Tell us something about yourself..."
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default AccountSettings;
